import React from "react";
import { TREND_LABELS } from "../models/forecastResult";

function fmt(n) {
  if (n == null || Number.isNaN(n)) return "—";
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 });
}

const COLUMNS = [
  { key: "itemName", label: "Item Name", align: "left" },
  { key: "currentStock", label: "Current Stock", align: "right" },
  { key: "avgMonthlySales", label: "Avg Monthly Sales", align: "right" },
  { key: "avgDailySales", label: "Avg Daily Sales", align: "right" },
  { key: "forecast15Days", label: "Forecast 15 Days", align: "right" },
  { key: "safetyStock", label: "Safety Stock", align: "right" },
  { key: "recommendedQty", label: "Recommended Qty", align: "right" },
  { key: "purchaseQty", label: "Purchase Qty", align: "right" },
  { key: "trend", label: "Trend", align: "center" },
];

export default function ForecastTable({ rows, page, pageSize, onPageChange, onRowClick, loading }) {
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const slice = rows.slice((safePage - 1) * pageSize, safePage * pageSize);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-600">
        <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mb-3" />
        <p className="text-sm">Loading sales history and computing forecasts…</p>
      </div>
    );
  }

  if (!rows.length) {
    return (
      <div className="text-center py-16 text-gray-500 border border-dashed border-gray-300 rounded-lg bg-white">
        <p className="text-lg mb-1">No forecast data</p>
        <p className="text-sm">Upload an Excel file with ItemName column to get started.</p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-300 rounded-lg shadow-sm overflow-hidden">
      <div className="overflow-x-auto max-h-[calc(100vh-320px)]">
        <table className="min-w-full text-xs border-collapse">
          <thead className="bg-slate-800 text-white sticky top-0 z-10">
            <tr>
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  className={`px-3 py-2 font-semibold whitespace-nowrap ${
                    col.align === "right" ? "text-right" : col.align === "center" ? "text-center" : "text-left"
                  }`}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {slice.map((row) => (
              <tr
                key={row.itemName}
                onClick={() => onRowClick?.(row)}
                className="border-b border-gray-100 hover:bg-indigo-50 cursor-pointer odd:bg-white even:bg-slate-50"
              >
                <td className="px-3 py-2 font-medium text-slate-800">{row.itemName}</td>
                <td className="px-3 py-2 text-right">{fmt(row.currentStock)}</td>
                <td className="px-3 py-2 text-right">{fmt(row.avgMonthlySales)}</td>
                <td className="px-3 py-2 text-right">{fmt(row.avgDailySales)}</td>
                <td className="px-3 py-2 text-right font-semibold text-indigo-700">
                  {fmt(row.forecast15Days)}
                </td>
                <td className="px-3 py-2 text-right">{fmt(row.safetyStock)}</td>
                <td className="px-3 py-2 text-right font-semibold">{fmt(row.recommendedQty)}</td>
                <td className="px-3 py-2 text-right text-emerald-700 font-semibold">
                  {fmt(row.purchaseQty)}
                </td>
                <td className="px-3 py-2 text-center whitespace-nowrap">
                  {TREND_LABELS[row.trend] || row.trend}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between px-3 py-2 border-t bg-slate-50 text-xs">
        <span>
          {rows.length.toLocaleString()} items · page {safePage} of {totalPages}
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={safePage <= 1}
            onClick={() => onPageChange(safePage - 1)}
            className="px-2 py-1 border rounded disabled:opacity-40"
          >
            Prev
          </button>
          <button
            type="button"
            disabled={safePage >= totalPages}
            onClick={() => onPageChange(safePage + 1)}
            className="px-2 py-1 border rounded disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

export function forecastRowsForExport(rows) {
  return rows.map((r) => ({
    "Item Name": r.itemName,
    Branch: r.branchName,
    "Current Stock": r.currentStock ?? "",
    "Total Sales": r.totalSales,
    "Avg Monthly Sales": Number(r.avgMonthlySales.toFixed(2)),
    "Avg Daily Sales": Number(r.avgDailySales.toFixed(2)),
    "Last 3 Mo Avg": Number(r.last3MonthsAvg.toFixed(2)),
    "Same Mo Prev Year": r.sameMonthPrevYearSales ?? "",
    "Growth %": r.growthPercent != null ? Number(r.growthPercent.toFixed(1)) : "",
    "Forecast 15 Days": Number(r.forecast15Days.toFixed(2)),
    "Safety Stock": Number(r.safetyStock.toFixed(2)),
    "Recommended Qty": r.recommendedQty,
    "Purchase Qty": r.purchaseQty ?? "",
    Trend: TREND_LABELS[r.trend] || r.trend,
  }));
}
